import Spinner from "@/components/Spinner"
import {
  usePatientDetailPageDataQuery,
  useUpdateCustomerSupportNotesMutation,
} from "@/data/queries"
import cn from "@/utils/cn"
import { useEffect, useRef, useState } from "react"

type CustomerSupportNotesProps = {
  bloomlifeId: string
}

type SaveStatus = "idle" | "saving" | "saved"

const CustomerSupportNotes = ({ bloomlifeId }: CustomerSupportNotesProps) => {
  const { data: pageData } = usePatientDetailPageDataQuery(bloomlifeId)
  const notes = pageData?.patient.customerSupportNotes?.notes ?? ""

  const [editedNotes, setEditedNotes] = useState<string | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle")
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const localNotes = editedNotes ?? notes

  const { mutate: updateNotes } =
    useUpdateCustomerSupportNotesMutation(bloomlifeId)

  const handleNotesChange = (newNotes: string) => {
    setEditedNotes(newNotes)
    setSaveStatus("saving")

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      updateNotes(newNotes, {
        onSuccess: () => {
          setSaveStatus("saved")
          setTimeout(() => setSaveStatus("idle"), 2000)
        },
        onError: () => {
          setSaveStatus("idle")
        },
      })
    }, 1000)
  }

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  const lastTwoLines = () => {
    const lines = localNotes.split("\n")
    return lines.slice(-2).join("\n") || "No notes added"
  }

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  // Scroll to bottom when expanding
  useEffect(() => {
    if (isExpanded && textareaRef.current) {
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight
    }
  }, [isExpanded])

  return (
    <div
      className={cn(
        "fixed bottom-6 right-6 z-40 flex w-80 flex-col overflow-hidden rounded-xl border border-gray-300 bg-white shadow-lg transition-all duration-300 ease-in-out",
        isExpanded ? "h-[500px]" : "h-20"
      )}
    >
      {isExpanded ? (
        <>
          <div className="flex items-center justify-between border-b border-gray-300 bg-gray-50 px-4 py-2">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-primary">
                Customer Support Notes
              </h3>
              {saveStatus === "saving" && <Spinner className="h-4 w-4" />}
              {saveStatus === "saved" && (
                <span className="material-icons text-sm text-green-600">
                  check
                </span>
              )}
            </div>
            <button
              onClick={toggleExpanded}
              className="cursor-pointer flex text-primary/60 transition-colors hover:text-primary"
            >
              <span className="material-icons text-sm">
                keyboard_arrow_down
              </span>
            </button>
          </div>

          <textarea
            ref={textareaRef}
            value={localNotes}
            maxLength={1000}
            onChange={(e) => handleNotesChange(e.target.value)}
            className="flex-1 resize-none border-none p-4 text-sm text-primary focus:outline-hidden"
            placeholder="Add customer support notes here..."
          />
        </>
      ) : (
        <>
          <button
            onClick={toggleExpanded}
            className="cursor-pointer flex h-full flex-col justify-center px-4 py-2 text-left hover:bg-gray-50"
          >
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-bold text-primary">
                Customer Support Notes
              </span>
              <span className="material-icons text-icon-md text-primary/60">
                keyboard_arrow_up
              </span>
            </div>
            <p className="line-clamp-2 text-xs text-primary/70">
              {lastTwoLines()}
            </p>
          </button>
        </>
      )}
    </div>
  )
}

export default CustomerSupportNotes
